import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X } from 'lucide-react';
import api from '../utils/api';

const ArticleFeedEditor = ({ content, onChange }) => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/articles/categories/list');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const updateContent = (key, value) => {
    onChange({ ...content, [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* Display Settings */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Display Settings</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {/* Layout Style */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Layout</Label>
            <Select
              value={content.layout || 'grid'}
              onValueChange={(value) => updateContent('layout', value)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Card Grid</SelectItem>
                <SelectItem value="list">List View</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Columns (for grid) */}
          {(content.layout || 'grid') === 'grid' && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Columns</Label>
              <Select
                value={String(content.columns || 3)}
                onValueChange={(value) => updateContent('columns', parseInt(value))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Columns</SelectItem>
                  <SelectItem value="3">3 Columns</SelectItem>
                  <SelectItem value="4">4 Columns</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Number of Articles */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Show Articles</Label>
            <Input
              type="number"
              value={content.limit || 6}
              onChange={(e) => updateContent('limit', parseInt(e.target.value) || 6)}
              className="h-8 text-xs"
              min="1"
              max="50"
            />
          </div>
        </div>
      </div>

      {/* Filter Settings */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Filter & Search</span>
        </div>
        
        <div className="grid grid-cols-1 gap-3">
          {/* Category Filter - Multi-select */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Filter by Categories</Label>
            {(() => {
              // Support both old single categoryFilter and new array categoryFilters
              const selected = content.categoryFilters || (content.categoryFilter ? [content.categoryFilter] : []);
              
              const toggleCategory = (cat) => {
                const updated = selected.includes(cat)
                  ? selected.filter(c => c !== cat)
                  : [...selected, cat];
                onChange({ ...content, categoryFilters: updated, categoryFilter: updated[0] || '' });
              };
              
              const clearAll = () => {
                onChange({ ...content, categoryFilters: [], categoryFilter: '' });
              };

              return (
                <div>
                  {selected.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {selected.map(cat => (
                        <span key={cat} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                          {cat}
                          <button onClick={() => toggleCategory(cat)} className="hover:text-indigo-900">
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      <button onClick={clearAll} className="text-xs text-slate-400 hover:text-slate-600 px-1">Clear all</button>
                    </div>
                  )}
                  <div className="max-h-32 overflow-y-auto border rounded-md bg-white">
                    {categories.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-slate-400">No categories found</p>
                    ) : categories.map(cat => (
                      <label key={cat} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={selected.includes(cat)}
                          onChange={() => toggleCategory(cat)}
                          className="w-3 h-3 rounded"
                        />
                        {cat}
                      </label>
                    ))}
                  </div>
                  {selected.length === 0 && (
                    <p className="text-xs text-slate-400 mt-1">No filter — showing all categories</p>
                  )}
                </div>
              );
            })()}
          </div>
          
          {/* Show Search */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Search Bar</Label>
            <Select
              value={content.showSearch ? 'yes' : 'no'}
              onValueChange={(value) => updateContent('showSearch', value === 'yes')}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">Hidden</SelectItem>
                <SelectItem value="yes">Show Search</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Show Public Category Filter */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Public Category Filter</Label>
            <Select
              value={content.showCategoryFilter ? 'yes' : 'no'}
              onValueChange={(value) => updateContent('showCategoryFilter', value === 'yes')}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">Hidden</SelectItem>
                <SelectItem value="yes">Show Filter Buttons</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">
              {content.showCategoryFilter 
                ? 'Visitors can filter articles by category on the public site' 
                : 'Category filter is hidden from visitors'}
            </p>
          </div>
        </div>
      </div>

      {/* Click Behavior */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Click Behavior</span>
        </div>
        
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">When article is clicked</Label>
          <Select
            value={content.openAs || 'popup'}
            onValueChange={(value) => updateContent('openAs', value)}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popup">Open as Popup</SelectItem>
              <SelectItem value="newpage">Open in New Page</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">
            {content.openAs === 'newpage' 
              ? 'Article opens in a dedicated page with full URL' 
              : 'Article opens in a modal overlay on the current page'}
          </p>
        </div>
      </div>

      {/* Pagination Settings */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Pagination</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {/* Enable Pagination */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Pagination</Label>
            <Select
              value={content.enablePagination ? 'yes' : 'no'}
              onValueChange={(value) => updateContent('enablePagination', value === 'yes')}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">Disabled</SelectItem>
                <SelectItem value="yes">Enabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Items Per Page */}
          {content.enablePagination && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Per Page</Label>
              <Input
                type="number"
                value={content.perPage || 6}
                onChange={(e) => updateContent('perPage', parseInt(e.target.value) || 6)}
                className="h-8 text-xs"
                min="1"
                max="50"
              />
            </div>
          )}
        </div>
      </div>

      {/* Display Options */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Card Options</span>
        </div>
        
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={content.showImage !== false}
              onChange={(e) => updateContent('showImage', e.target.checked)}
              className="w-3 h-3 rounded"
            />
            Show featured images
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={content.showDate !== false}
              onChange={(e) => updateContent('showDate', e.target.checked)}
              className="w-3 h-3 rounded"
            />
            Show article date
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={content.showExcerpt !== false}
              onChange={(e) => updateContent('showExcerpt', e.target.checked)}
              className="w-3 h-3 rounded"
            />
            Show excerpt/preview text
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={content.showCategory !== false}
              onChange={(e) => updateContent('showCategory', e.target.checked)}
              className="w-3 h-3 rounded"
            />
            Show category badge
          </label>
        </div>
      </div>

      {/* Heading */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-slate-700">Section Heading (Optional)</span>
        </div>
        <Input
          value={content.heading || ''}
          onChange={(e) => updateContent('heading', e.target.value)}
          placeholder="e.g., Latest Articles, News, Blog Posts..."
          className="h-8 text-xs"
        />
      </div>

      {/* Preview Info */}
      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
        <p className="text-xs text-indigo-700">
          <strong>Note:</strong> This block will display published articles from your Articles section. 
          Create and publish articles in the Articles menu to see them here.
        </p>
      </div>
    </div>
  );
};

export default ArticleFeedEditor;
